import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectCaseStudy.module.css';

const IMG_BASE = "/assets/project/Thesis 'AI‑Driven Predictive Maintenance for Aircraft Engines'";

const Figure = ({ src, alt, caption }) => (
  <figure className={styles.figureCard}>
    <img src={src} alt={alt} className={styles.galleryImage} />
    <figcaption className={styles.galleryCaption}>
      {caption}
    </figcaption>
  </figure>
);

export default function E2EPdMProjectPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/projects')}>
        ← Back to Projects
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>
          A Novel Integrated ML Framework with
          Comparative Evaluation for Fault Detection,
          Condition Monitoring, and RUL Prediction of
          Turbofan Engines
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee</span>,{' '}
          <span>Md Hemal Ishak</span>,{' '}
          <span>Arobi Sultana Oishy</span>,{' '}
          <span>Air Cdre Dr. Saifur Rahman Bakaul</span>
        </div>
        <div className={styles.affiliations}>
          Department of Aerospace Engineering, Aviation and Aerospace University, Bangladesh (AAUB)
        </div>
        <div className={styles.badge}>
          🏆 Best Thesis Award 2025, AAUB
        </div>
      </header>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Abstract</h3>
        <p className={styles.abstractText}>
          Existing prognostics and health management research predominantly treats anomaly detection, condition monitoring, and remaining useful life (RUL) prediction as isolated analytical tasks rather than interdependent components of a real-world system. This study presents a unified, end-to-end predictive maintenance framework that integrates these three core PHM capabilities, evaluated using the NASA C-MAPSS dataset. The framework combines data preprocessing, feature engineering, a life ratio-based health labeling strategy, SHAP-based interpretability, and comparative model evaluation. An Isolation Forest model is utilized for anomaly detection. For condition monitoring, a bidirectional LSTM (Bi-LSTM) network captures complex sequential degradation, achieving 98.5% accuracy in classifying engine health states. An LSTM network is deployed for RUL prediction, achieving an RMSE of 13.54 — a new benchmark on the C-MAPSS FD001 dataset. Finally, these models are integrated and deployed into a real-time API architecture using FastAPI and Streamlit so maintenance teams can monitor a fleet live.
        </p>
        <div className={styles.keywords}>
          <strong>Keywords:</strong> Predictive Maintenance, Prognostics and Health Management, Turbofan Engines, NASA C-MAPSS, Anomaly Detection, Condition Monitoring, Remaining Useful Life, Bi-LSTM, LSTM, Isolation Forest
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>Research Framework</h2>
        <p className={styles.paragraph}>
          The proposed framework unifies four core PHM capabilities — data ingestion & preprocessing, anomaly detection, condition monitoring, and RUL estimation — under a single pipeline. After EDA and multivariate feature engineering, the dataset is fed into parallel algorithmic branches. Each branch undergoes rigorous hyperparameter tuning with K-fold cross-validation, followed by PCA-based dimensionality reduction to select the best-performing model. The final optimized models are exposed through a unified Engine Condition Monitoring API, enabling real-time fleet-wide diagnostics.
        </p>
        <figure className={styles.figureCard} style={{ margin: '1.5rem 0' }}>
          <img
            src={`${IMG_BASE}/Research Framework.png`}
            alt="End-to-End Predictive Maintenance Research Framework"
            style={{ width: '100%', maxHeight: '420px', objectFit: 'contain', background: '#fff', borderRadius: '6px' }}
          />
          <figcaption className={styles.galleryCaption}>
            Figure 1: Complete end-to-end predictive maintenance framework integrating anomaly detection, condition monitoring, RUL estimation, and SHAP-based interpretability.
          </figcaption>
        </figure>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Core Results</h2>
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>98.5%</div>
            <div className={styles.metricLabel}>Bi-LSTM CM Accuracy</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>13.54</div>
            <div className={styles.metricLabel}>LSTM RUL RMSE</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>0.89</div>
            <div className={styles.metricLabel}>LSTM R² Score</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>83%</div>
            <div className={styles.metricLabel}>Isolation Forest AD Accuracy</div>
          </div>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Anomaly Detection</h2>
        <p className={styles.paragraph}>
          Four classical unsupervised models — Isolation Forest, Local Outlier Factor (LOF), One-Class SVM, and Elliptic Envelope — were evaluated for detecting anomalous engine behavior in the C-MAPSS FD001 dataset. Isolation Forest emerged as the top performer with an AUC of 0.92 and an average precision of 0.77, significantly outperforming the other candidates. The anomaly score distribution reveals a clear bimodal separation, enabling reliable threshold-based classification between normal and degraded engine states. For a deeper dive into the anomaly detection methodology and comparative analysis, visit the <button onClick={() => navigate('/research/ai-anomaly-detection')} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}>AI Anomaly Detection research page</button>.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/Anomaly_Score_Distribution_case_one.png`}
            alt="Anomaly Score Distribution"
            caption="Figure 2: Anomaly score distribution for Case One (FD001) showing clear separation between normal and anomalous engine behavior."
          />
          <Figure
            src={`${IMG_BASE}/Confusion_Matrix_for_all_models_of_Case_one_After_Hyperparameter_Tuning.png`}
            alt="Confusion Matrices"
            caption="Figure 3: Confusion matrices for Isolation Forest, LOF, One-Class SVM, and Elliptic Envelope after Bayesian hyperparameter tuning."
          />
          <Figure
            src={`${IMG_BASE}/ROC_Curves_for_all_models_of_Case_one.png`}
            alt="ROC Curves"
            caption="Figure 4: ROC curves comparing four anomaly detection models. Isolation Forest achieves AUC = 0.92, far exceeding the random guess baseline."
          />
          <Figure
            src={`${IMG_BASE}/Precision_Recall_Curves_for_all_models_of_Case_one.png`}
            alt="Precision-Recall Curves"
            caption="Figure 5: Precision-Recall curves showing Isolation Forest (AP = 0.77) outperforming LOF, One-Class SVM, and Elliptic Envelope."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Condition Monitoring</h2>
        <p className={styles.paragraph}>
          A life-ratio-based health labeling strategy was developed to classify engine health into three states: Good (LR &lt; 0.6), Moderate (0.6 ≤ LR &lt; 0.8), and Alarming (LR ≥ 0.8). Eleven classical and four deep-learning classifiers were trained and validated using 5-fold cross-validation. The proposed Bi-LSTM architecture achieved 98.5% test accuracy and an AUC of 0.999, demonstrating superior capacity to capture bidirectional temporal degradation patterns compared to Gradient Boosting, XGBoost, and KNN benchmarks.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/Condition Monitoring Framework.png`}
            alt="Condition Monitoring Framework"
            caption="Figure 6: Condition monitoring framework with life-ratio-based health labeling, train-test split, and K-fold cross-validation pipeline."
          />
          <Figure
            src={`${IMG_BASE}/performance comparison cm.png`}
            alt="Performance Comparison"
            caption="Figure 7: Model performance comparison for condition monitoring. Proposed Bi-LSTM achieves 98.5% test accuracy, outperforming all classical ensembles."
          />
          <Figure
            src={`${IMG_BASE}/roc_pr_curves for CM.png`}
            alt="ROC and PR Curves for CM"
            caption="Figure 8: Macro-average ROC (AUC = 0.999) and Precision-Recall (AP = 0.997) curves for the proposed Bi-LSTM condition monitor."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>RUL Prediction & Correlation Analysis</h2>
        <p className={styles.paragraph}>
          For prognostics, an LSTM regressor was trained on multivariate time-series windows to predict Remaining Useful Life. The model achieved an RMSE of 13.54 cycles and an R² of 0.89 on the FD001 test set — establishing a new benchmark for this dataset. A comprehensive correlation analysis was performed to identify the 13 primary degradation-sensitive sensors, which were subsequently used for feature engineering and SHAP-based model interpretability.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/actual vs predicted rul.png`}
            alt="Actual vs Predicted RUL"
            caption="Figure 9: Actual vs. predicted RUL comparison across 100 engine units, demonstrating strong agreement (LSTM, RMSE = 13.54)."
          />
          <Figure
            src={`${IMG_BASE}/Correlation_Case_One.png`}
            alt="Correlation Heatmap"
            caption="Figure 10: Sensor correlation heatmap for Case One revealing critical degradation features (e.g., sensor_11, sensor_4, sensor_12) used for feature engineering."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>System Architecture & Real-Time Deployment</h2>
        <p className={styles.paragraph}>
          The framework was deployed as a unified, API-driven architecture. A FastAPI backend serves the trained anomaly detection, condition monitoring, and RUL prediction models through REST endpoints. A Streamlit-based frontend provides the Turbofan Engine Diagnostic Dashboard, rendering real-time telemetry plots, health status alerts, and RUL gauges for fleet managers. The cloud-native pipeline bridges the gap between research prototypes and operational maintenance scheduling.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/pdm system architecture.png`}
            alt="PdM System Architecture"
            caption="Figure 11: Cloud-based PdM system architecture connecting aircraft telemetry, gateway, cloud server, and maintenance scheduling."
          />
          <Figure
            src={`${IMG_BASE}/cover.png`}
            alt="Turbofan Diagnostic Dashboard"
            caption="Figure 12: Real-time Turbofan Engine Diagnostic Dashboard displaying critical health features, anomaly status, and RUL estimation for Engine 100."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Research Papers</h2>
        <p className={styles.paragraph}>
          The complete journal manuscript detailing the integrated methodology and results is currently under peer review at <em>Journal of Aerospace Information Systems, AIAA</em>, and a companion conference paper on the anomaly detection subsystem has been published in IEEE Xplore. Explore both research pages below:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
          <div style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '1.3rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem',
            boxShadow: 'var(--shadow-xs)'
          }}>
            <div style={{ flex: '1 1 320px' }}>
              <h4 style={{ margin: '0 0 0.35rem 0', fontSize: '1.02rem', color: 'var(--fg)' }}>
                A Novel AI-Driven Anomaly Detection Framework for Predictive Maintenance of Aircraft Engines
              </h4>
              <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                2025 11th International Conference on Mechanical Engineering and Aerospace Engineering, Beijing, China, IEEE Xplore — Published (DOI: 10.1109/MEAE68077.2025.11557878).
              </p>
            </div>
            <button
              onClick={() => navigate('/research/ai-anomaly-detection')}
              className={styles.btnPrimary}
              style={{ padding: '0.5rem 1.05rem', fontSize: '0.85rem' }}
            >
              Visit Research Page →
            </button>
          </div>

          <div style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '1.3rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem',
            boxShadow: 'var(--shadow-xs)'
          }}>
            <div style={{ flex: '1 1 320px' }}>
              <h4 style={{ margin: '0 0 0.35rem 0', fontSize: '1.02rem', color: 'var(--fg)' }}>
                An AI-Driven Integrated Framework for Turbofan Engine Health Monitoring, Fault Diagnosis, and Prognostics
              </h4>
              <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                Journal of Aerospace Information Systems, AIAA (2026) — Under Review. Full integrated framework covering anomaly detection, condition monitoring, and RUL prediction with SHAP explainability.
              </p>
            </div>
            <button
              onClick={() => navigate('/research/e2e-pdm-framework')}
              className={styles.btnPrimary}
              style={{ background: '#d97706', borderColor: '#d97706', padding: '0.5rem 1.05rem', fontSize: '0.85rem' }}
            >
              Visit Research Page →
            </button>
          </div>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          This work demonstrates that integrating anomaly detection, condition monitoring, and RUL prediction into a single framework yields significant operational advantages over siloed PHM approaches. The Isolation Forest–Bi-LSTM–LSTM pipeline, combined with SHAP explainability and real-time API deployment, provides a scalable blueprint for next-generation aerospace predictive maintenance systems. The achieved RMSE of 13.54 on C-MAPSS FD001 represents a new performance benchmark, while the deployed dashboard validates the practical viability of the research in real-world maintenance environments.
        </p>
        <p className={styles.paragraph} style={{ fontWeight: 600, color: 'var(--fg)' }}>
          👉 To explore the full publication details, methodology, and peer-review status, please visit the <button onClick={() => navigate('/research/e2e-pdm-framework')} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}>Integrated Turbofan Framework research page</button> and the <button onClick={() => navigate('/research/ai-anomaly-detection')} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}>AI Anomaly Detection research page</button>.
        </p>
      </section>

    </div>
  );
}